import { useMemo } from 'react';
import {  useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { prettyMoney } from '@scramble/utils';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useIsMobile } from '@scramble/hooks';
import { RadioButton } from '@scramble/ui';
import { useGetPaymentMethods } from '@/query/queries/cash/useGetPaymentMethods';
import { useGetPaymentCalculatedAmount } from '@/query/queries/cash/useGetPaymentCalculatedAmount';
import { Preloader } from '@/components/Preloader';
import {
  calculateFee,
  getProcessingTime,
} from '@/pages/FundsAddPage/utils';
import s from './index.module.scss';

const id = MODAL_IDS.payment_method_select_modal;

export const PaymentMethodSelectModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = ({ amount, selected, onChange }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetPaymentMethods();
  const isMobile = useIsMobile();
  const { closeModal } = useModal();

  let content;

  if (isLoading || !data) {
    content = (
      <div className={s.modal}>
        <div className={s.header}>
          <h1 className={s.title}>
            {t('modal:payment_method_selection_modal.title')}
          </h1>
        </div>
        <div className={s.content}>
          <div className={s.content_inner}>
            <Preloader className={s.preloader} />
          </div>
        </div>
      </div>
    );
  } else {
    content = (
      <div className={s.modal}>
        <div className={s.header}>
          <h1 className={s.title}>
            {t('modal:payment_method_selection_modal.title')}
          </h1>
        </div>
        <div className={s.content}>
          <div className={s.content_inner}>
            {data?.map(item => (
              <PaymentMethod
                key={item.id}
                amount={amount}
                item={item}
                selected={selected}
                onChange={onChange}
                onClose={() => closeModal(id)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};

const PaymentMethod = ({ item, amount, selected, onChange, onClose }) => {
  const { t } = useTranslation();
  const { data: calculation } = useGetPaymentCalculatedAmount({
    amount,
    payment_method: item.type,
  });

  const feeText = useMemo(() => {
    if (!calculation) return '';
    const totalFee = calculation.amount;
    const fee = calculateFee(totalFee, amount);
    if (fee <= 0) return t('funds:payment_method.no_fee');
    if (fee > 0) return prettyMoney(fee);
    return '';
  }, [amount, calculation]);

  return (
    <div
      className={s.card}
      key={item.type}
      onClick={() => {
        onChange(item);
        onClose();
      }}
    >
      <div className={s.card_content}>
        <img src={item.icon} width={20} height={20} alt="icon" />
        <div>
          <div className={s.card_title}>{item.title}</div>
          <div className={s.card_desc}>{item.desc}</div>
          <div className={s.card_summary}>
            <span>{getProcessingTime(item.type)}</span>
            {feeText && <span>{feeText}</span>}
          </div>
        </div>
      </div>
      <RadioButton
        name="payment"
        className={s.card_radio}
        checked={item.type === selected?.type}
        onChange={() => null}
      />
    </div>
  );
}